import { isAxiosError } from 'axios';
import { useEffect, useRef } from 'react';
import { openModal } from 'reducers/modalReducer';
import { useDispatch } from 'react-redux';
import { serializeError } from 'utils/serializeError';

const buildSerializationFallbackDetails = (error: unknown, serializationError: unknown): string =>
  [
    `name: SerializationFailure`,
    `message: Failed to serialize non-HTTP error payload.`,
    `serializationError: ${
      serializationError instanceof Error ? serializationError.message : String(serializationError)
    }`,
    `rawErrorType: ${Object.prototype.toString.call(error)}`,
    `rawErrorValue: ${String(error)}`,
  ].join('\n');

const buildNonHttpErrorDetails = (error: unknown): string => {
  if (error instanceof Error) {
    return [
      `name: ${error.name || 'UnknownError'}`,
      `message: ${error.message || 'No message provided'}`,
      `stack: ${error.stack || 'No stack trace available'}`,
      `serializedError: ${serializeError(error)}`,
    ].join('\n');
  }

  return [`name: NonErrorObject`, `message: Non-Error value thrown`, `serializedError: ${serializeError(error)}`].join(
    '\n',
  );
};

export function useErrorHandling(error: unknown | undefined, callback: () => void) {
  const dispatch = useDispatch();
  const lastHandledErrorRef = useRef<unknown>(undefined);

  useEffect(() => {
    if (error === undefined) {
      lastHandledErrorRef.current = undefined;
      return;
    }

    if (lastHandledErrorRef.current === error) {
      return;
    }

    lastHandledErrorRef.current = error;

    const isHttpError = isAxiosError(error);
    let nonHttpDetails = '';

    if (!isHttpError) {
      console.error('[useErrorHandling] Non-HTTP error captured', error);
      try {
        nonHttpDetails = buildNonHttpErrorDetails(error);
      } catch (serializationError) {
        console.error('[useErrorHandling] Failed to serialize non-HTTP error payload', {
          error,
          serializationError,
        });
        nonHttpDetails = buildSerializationFallbackDetails(error, serializationError);
      }
    }

    dispatch(
      openModal({
        modalType: 'JBErrorModalWrapper',
        modalProps: {
          httpError: error,
          okHandler: callback,
          technicalInfo: {
            title: isHttpError ? 'Unknown error' : 'Client runtime error',
            userErrorMessage: isHttpError
              ? 'An unknown error occurred. Please try again later.'
              : 'A technical problem occurred in the application. Please try again later.',
            ...(isHttpError ? {} : { details: nonHttpDetails }),
          },
        },
      }),
    );
  }, [error, callback, dispatch]);
}
